<script setup lang="ts">
/**
 * Un mouvement dans l'historique : type explicite par l'icône et le signe, enveloppe, heure,
 * montant, et statut de synchronisation quand il n'est pas encore confirmé par le serveur.
 */
import { computed } from 'vue'

import { fr } from '@/copy/fr'

import type { IconName } from '../icons/registry'
import Amount from './Amount.vue'
import ListRow from './ListRow.vue'
import SyncBadge from './SyncBadge.vue'

export type MovementKind =
  'income' | 'expense' | 'transfer' | 'allocation' | 'adjustment' | 'reversal'

const props = withDefaults(
  defineProps<{
    kind: MovementKind
    title: string
    detail: string
    amount: number
    sync?: 'synced' | 'pending' | 'conflict'
    reversed?: boolean
  }>(),
  { sync: 'synced', reversed: false },
)

defineEmits<{ click: [event: MouseEvent] }>()

const ICONS: Record<MovementKind, IconName> = {
  income: 'income',
  expense: 'expense',
  transfer: 'transfer',
  allocation: 'envelopes',
  adjustment: 'edit',
  reversal: 'refresh',
}

const signed = computed(() =>
  props.kind === 'income' ? 'income' : props.kind === 'expense' ? 'expense' : 'none',
)
</script>

<template>
  <ListRow :title="title" :icon="ICONS[kind]" interactive @click="(event) => $emit('click', event)">
    <template #subtitle>
      <span class="flex items-center gap-2">
        <span class="truncate">{{ reversed ? `${detail} · ${fr.ui.reversed}` : detail }}</span>
        <SyncBadge v-if="sync !== 'synced'" :status="sync" />
      </span>
    </template>
    <template #trailing>
      <Amount
        :value="amount"
        size="body"
        :signed="signed"
        :tone="reversed ? 'muted' : kind === 'income' ? 'positive' : 'default'"
        :class="reversed ? 'line-through' : ''"
      />
    </template>
  </ListRow>
</template>
